import { useApi } from "@platform/api";
import {
  upsertRfpNote,
  RfpNotePayload,
  getRfpNote,
} from "@platform/btx-plan-designer";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export type UseRfpNoteResult = {
  content: string;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  upsertNoteAsync: (request: RfpNotePayload) => Promise<void>;
  isUpserting: boolean;
  noteResponse?: RfpNotePayload;
};

/**
 * Hook for fetching and upserting a note for an RFP.
 * Operates on the note associated with the provided rfpId.
 */
export function useRfpNote(rfpId: string): UseRfpNoteResult {
  const queryClient = useQueryClient();
  const axiosInstance = useApi();
  const queryKey = ["rfp-notes", rfpId];
  const query = useQuery<RfpNotePayload, Error>({
    queryKey,
    queryFn: () => getRfpNote(rfpId, axiosInstance),
    enabled: !!rfpId,
  });

  const mutation = useMutation({
    mutationFn: (request: RfpNotePayload) =>
      upsertRfpNote(rfpId, request, axiosInstance),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    content: typeof query.data?.content === "string" ? query.data.content : "",
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
    refetch: query.refetch,
    upsertNoteAsync: async (request: RfpNotePayload) => {
      await mutation.mutateAsync(request);
    },
    isUpserting: mutation.isPending,
    noteResponse:
      query.data && typeof query.data === "object"
        ? (query.data as RfpNotePayload)
        : undefined,
  };
}
